import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Taskbar } from '../Taskbar/Taskbar';
import { DesktopIcon } from './DesktopIcon';
import { RecycleBinWindow } from '../Window/RecycleBinWindow';
import { Notepad } from '../Apps/Notepad';
import { CMD } from '../Apps/CMD';
import { About } from '../Apps/About';
import { ProjectsFolder } from '../Apps/ProjectsFolder';
import { Resume } from '../Apps/Resume';
import { ChessGame } from '../Apps/Chess';
import { DavidOSInfo } from '../Apps/DavidOSInfo';
import { AppleFarmInfo } from '../Apps/AppleFarmInfo';
import { Help } from '../Apps/Help';
import { RestartScreen } from '../Apps/RestartScreen';
import { createIconBounceAnimation, taskbarChaosAnimation, staticEffect } from '../../styles/meltEffects';
import { MP3Player } from '../Apps/MP3Player';
// import Game3D from '../Apps/Game3D';

const DesktopContainer = styled.div`
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background-color:rgb(2, 41, 2);  // Very dark green base
  background-image: linear-gradient(
    0deg,
    rgba(128, 128, 128, 0.2) 1.5px,
    transparent 2px
  ),
  linear-gradient(
    90deg,
    rgba(128, 128, 128, 0.2) 1.5px,
    transparent 2px
  );
  background-size: 75px 75px;  // Larger grid for subtlety
  background-position: center center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      rgba(0, 40, 20, 0.2) 0%,
      rgba(0, 0, 0, 0.4) 100%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    pointer-events: none;
    width: 100%;
  }
`;

const WatermarkContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  gap: 20px;
  opacity: 0.85;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    
    img {
      width: 80px;
      height: 80px;
    }

    span {
      font-size: 24px;
    }
  }

  img {
    width: 120px;
    height: 120px;
  }

  span {
    font-family: 'Press Start 2P', monospace;
    font-size: 48px;
    color: white;
    white-space: nowrap;
    text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.3);
  }
`;

const MeltingDesktop = styled(DesktopContainer)`
  &.melting {
    position: relative;
    overflow: hidden;

    .desktop-icon {
      position: absolute;
      will-change: transform;
      transition: none !important;
    }

    .taskbar {
      animation: ${taskbarChaosAnimation} 0.3s ease-in-out forwards;
      animation-delay: 0.5s;
      position: fixed !important;
      bottom: 0 !important;
      max-height: 40px;
      overflow: hidden;
    }

    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: repeating-linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.1),
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
      pointer-events: none;
      animation: ${staticEffect} 0.2s steps(2) infinite;
      z-index: 9997;
    }
  }
`;

const DesktopContent = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - 40px);
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 5px;
  overflow: hidden;
  user-select: none; // Prevent text selection while dragging

  /* Add this media query for medium screens */
  @media (max-width: 1024px) {
    flex-direction: row;
    justify-content: flex-start;
    gap: 20px;
    padding: 15px;

    /* Reset absolute positioning on medium screens */
    & > * {
      position: relative !important;
      left: auto !important;
      top: auto !important;
    }
  }

  /* Keep the grid layout for mobile */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    gap: 10px;
    padding: 10px;
    overflow-y: auto;
    align-items: start;
    justify-items: center;

    /* Reset absolute positioning on mobile */
    & > * {
      position: relative !important;
      left: auto !important;
      top: auto !important;
    }
  }

  /* Remove any cursor: move if it exists */
  & > * {
    cursor: pointer;  // Change to pointer to indicate clickable
    
    @media (max-width: 1024px) {
      position: relative !important;
      left: auto !important;
      top: auto !important;
    }
  }
`;

const WindowsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 40px;
  pointer-events: none;
  
  &.melting {
    animation: shake 0.5s ease-in-out infinite;
    
    & > * {
      animation: windowChaos 3s ease-in-out infinite;
      transform-origin: center center;
    }
  }

  & > * {
    pointer-events: auto;
  }

  @keyframes shake {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(-5px, 3px) rotate(-1deg); }
    75% { transform: translate(5px, -3px) rotate(1deg); }
  }

  @keyframes windowChaos {
    0% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(-10px, 5px) rotate(-2deg); }
    75% { transform: translate(10px, -5px) rotate(2deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }
`;

const IconLabel = styled.div`
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
  margin-top: 5px;
  font-family: 'VT323', monospace;
  font-size: 14px;
`;

const ContextMenuWrapper = styled.div`
  position: fixed;
  background: #c0c0c0;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  padding: 2px;
  min-width: 100px;
  z-index: 1000;
`;

const MenuItem = styled.div`
  padding: 4px 20px;
  &:hover {
    background: #000080;
    color: white;
  }
`;

const ContextMenu = ({ x, y, onDelete }) => {
  return (
    <ContextMenuWrapper style={{ left: x, top: y }}>
      <MenuItem onClick={onDelete}>Delete</MenuItem>
    </ContextMenuWrapper>
  );
};

const GRID_SIZE = 75; // Matches your desktop grid size
const ICON_SIZE = 64; // Typical size for desktop icons

const SelectionRectangle = styled.div`
  position: fixed;
  border: 1px dotted #ffffff;
  background: rgba(51, 153, 255, 0.3);  // Changed from rgba(0, 0, 128, 0.3) to a lighter blue
  pointer-events: none;
  z-index: 2;
`;

export const Desktop = () => {
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [openWindows, setOpenWindows] = useState([]);
  const [activeWindow, setActiveWindow] = useState(null);
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [windowPositions, setWindowPositions] = useState({});
  const [iconPositions, setIconPositions] = useState({});
  const [draggingIcon, setDraggingIcon] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMelting, setIsMelting] = useState(false);
  const [showRestartScreen, setShowRestartScreen] = useState(false);
  const [iconVelocities, setIconVelocities] = useState({});
  const animationFrameRef = useRef();
  const mp3Ref = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });
  const desktopContentRef = useRef(null);

  const getDefaultWindowSize = (appName) => {
    switch(appName) {
      case 'Notepad':
        return { width: '600px', height: '400px' };
      case 'Command Prompt':
        return { width: '640px', height: '480px' };
      case 'About':
        return { width: '500px', height: '300px' };
      case 'Recycle Bin':
        return { width: '500px', height: '400px' };
      default:
        return { width: '600px', height: '400px' };
    }
  };

  const calculateWindowPosition = () => {
    const baseOffset = 25;
    const windowCount = openWindows.length;
    const maxWindows = 10; // Reset after this many windows
    
    // Calculate position with wrapping
    const offsetIndex = windowCount % maxWindows;
    
    // Start from a reasonable position and cascade
    const baseLeft = Math.max(50, (window.innerWidth - 600) / 3);
    const baseTop = Math.max(50, (window.innerHeight - 400) / 4);
    
    return {
      left: baseLeft + (baseOffset * offsetIndex),
      top: baseTop + (baseOffset * offsetIndex)
    };
  };

  const handleOpenApp = (appName) => {
    const { width, height } = getDefaultWindowSize(appName);
    const newWindow = {
      id: Date.now(),
      name: appName,
      isMinimized: false,
      isMaximized: false,
      defaultWidth: width,
      defaultHeight: height,
      content: ''
    };
    
    // Calculate and store position for new window
    const position = calculateWindowPosition();
    setWindowPositions(prev => ({
      ...prev,
      [newWindow.id]: position
    }));
    
    setOpenWindows(prev => [...prev, newWindow]);
    setActiveWindow(newWindow.id);
    setShowStartMenu(false);
  };

  const handleCloseWindow = (windowId) => {
    setOpenWindows(prev => prev.filter(window => window.id !== windowId));
    if (activeWindow === windowId) {
      setActiveWindow(null);
    }
  };

  const handleActivateWindow = (windowId) => {
    setActiveWindow(windowId);
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId 
        ? { ...window, isMinimized: false }
        : window
    ));
  };

  const handleContentChange = (windowId, newContent) => {
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId 
        ? { ...window, content: newContent }
        : window
    ));
  };

  const handleIconClick = (item) => {
    setSelectedIcon(item.label);
  };

  // Define which apps are currently implemented
  const implementedApps = {
    'Recycle Bin': true,
    'Notepad': true,
    'Command Prompt': true,
    'About Me': true,
    'Resume': true,
    'Projects': true,
    NOTEPAD: Notepad,
    CMD: CMD,
    RESUME: Resume,
    ABOUT: About,
    'Chess': true,
    CHESS: ChessGame,
    'DavidOSInfo': true,
    DAVIDOSINFO: DavidOSInfo,
    'AppleFarmInfo': true,
    APPLEFARMINFO: AppleFarmInfo,
    'MP3 Player': true,
    MP3PLAYER: MP3Player,
  };

  const desktopItems = [
    { icon: '/images/icons/recycle-bin.svg', label: 'Recycle Bin', type: 'system', enabled: true },
    { icon: '/images/icons/notepad.svg', label: 'Notepad', type: 'app', enabled: true },
    { icon: '/images/icons/cmd.svg', label: 'Command Prompt', type: 'app', enabled: true },
    { icon: '/images/icons/resume.svg', label: 'Resume', type: 'app', enabled: true },
    { icon: '/images/icons/folder.svg', label: 'Projects', type: 'folder', enabled: true },
    { icon: '/images/icons/chess.svg', label: 'Chess', type: 'app', enabled: true },
    { icon: '/images/icons/about.svg', label: 'About Me', type: 'app', enabled: true },
  ];

  const handleSaveFile = (filename, content) => {
    const newFile = {
      name: filename,
      content,
      icon: '/images/icons/text-file.svg',
      type: 'file'
    };
    setSavedFiles(prev => [...prev, newFile]);
  };

  // Modify your desktopItems to include saved files
  const allDesktopItems = [
    ...desktopItems,
    ...savedFiles.map(file => ({
      icon: file.icon,
      label: file.name,
      type: 'file',
      enabled: true,
      content: file.content
    }))
  ];

  const handleIconDoubleClick = (item) => {
    if (item.type === 'file') {
      const newWindow = {
        id: Date.now(),
        name: 'Notepad',
        content: item.content,
        isMinimized: false,
        isMaximized: false
      };
      setOpenWindows(prev => [...prev, newWindow]);
      setActiveWindow(newWindow.id);
    } else {
      if (implementedApps[item.label]) {
        handleOpenApp(item.label);
      } else {
        alert(`${item.label} is coming soon!`);
      }
    }
  };

  const handleMinimize = (windowId) => {
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId 
        ? { ...window, isMinimized: true }
        : window
    ));
  };

  const handleMaximize = (windowId) => {
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId 
        ? { ...window, isMaximized: !window.isMaximized }
        : window
    ));
  };

  const handleStartMenuItemClick = (item) => {
    console.log('Start menu item clicked:', item); // Debug log
    handleOpenApp(item.label);
  };

  const handleStartClick = () => {
    setShowStartMenu(prev => !prev);
  };

  const handleOpenAbout = () => {
    const newWindow = {
      id: Date.now(),
      name: 'About',
      isMinimized: false,
      isMaximized: false
    };
    setOpenWindows(prev => [...prev, newWindow]);
    setActiveWindow(newWindow.id);
  };

  const handleIconContextMenu = (e, item) => {
    e.preventDefault();
    if (item.type === 'file') {
      setSelectedFile(item);
      setContextMenuPosition({ x: e.clientX, y: e.clientY });
      setShowContextMenu(true);
    }
  };

  const handleDeleteFile = (filename) => {
    setSavedFiles(prev => prev.filter(file => file.name !== filename));
    setShowContextMenu(false);
  };

  // Add click handler to close context menu
  useEffect(() => {
    const handleClick = () => setShowContextMenu(false);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const handleOpenWindow = (event) => {
      if (event.detail.windowName === 'DavidOSInfo') {
        handleOpenApp('DavidOSInfo');
      } else if (event.detail.windowName === 'AppleFarmInfo') {
        handleOpenApp('AppleFarmInfo');
      }
    };

    window.addEventListener('openWindow', handleOpenWindow);
    return () => window.removeEventListener('openWindow', handleOpenWindow);
  }, []);

  const handleProjectFileOpen = (fileName) => {
    console.log('Opening project file:', fileName);
    if (fileName === 'davidos.txt') {
      handleOpenApp('DavidOSInfo');
    } else if (fileName === 'apple farm.txt') {
      handleOpenApp('AppleFarmInfo');
    }
  };

  const snapToGrid = (x, y) => {
    // Use a larger grid size for snapping to make it less restrictive
    const snapSize = GRID_SIZE * 1.5;
    return {
      x: Math.round(x / snapSize) * snapSize,
      y: Math.round(y / snapSize) * snapSize
    };
  };

  const isPositionOccupied = (x, y, excludeIcon) => {
    return Object.entries(iconPositions).some(([label, pos]) => {
      if (label === excludeIcon) return false;
      
      // Check if icons overlap
      const distance = Math.sqrt(
        Math.pow(pos.x - x, 2) + 
        Math.pow(pos.y - y, 2)
      );
      
      return distance < GRID_SIZE * 0.8; // Allow some overlap during drag
    });
  };

  const findNearestFreePosition = (x, y, iconLabel) => {
    // First try the exact position
    if (!isPositionOccupied(x, y, iconLabel)) {
      return { x, y };
    }

    // Search in a spiral pattern
    for (let radius = 1; radius <= 5; radius++) {
      // Try positions in a square around the target point
      for (let offsetX = -radius; offsetX <= radius; offsetX++) {
        for (let offsetY = -radius; offsetY <= radius; offsetY++) {
          // Skip positions we've already checked
          if (Math.abs(offsetX) < radius && Math.abs(offsetY) < radius) continue;
          
          const testX = x + (offsetX * GRID_SIZE);
          const testY = y + (offsetY * GRID_SIZE);
          
          if (!isPositionOccupied(testX, testY, iconLabel)) {
            return { x: testX, y: testY };
          }
        }
      }
    }

    // If no free position found, return original position
    return { x, y };
  };

  const handleDragStart = (e, item) => {
    setDraggingIcon(item.label);
    
    // Calculate offset from the mouse position to the icon's top-left corner
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleDrag = (e, item) => {
    if (!draggingIcon) return;
    
    // Calculate new position based on mouse position and initial offset
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Allow free movement during drag, including overlaps
    setIconPositions(prev => ({
      ...prev,
      [item.label]: { x: newX, y: newY }
    }));
  };

  const handleDragEnd = (e, item) => {
    if (!draggingIcon) return;

    // Calculate drop position
    const dropX = e.clientX - dragOffset.x;
    const dropY = e.clientY - dragOffset.y;

    // Find nearest free position only after dropping
    const snapped = snapToGrid(dropX, dropY);
    const finalPosition = findNearestFreePosition(snapped.x, snapped.y, item.label);

    setIconPositions(prev => ({
      ...prev,
      [item.label]: finalPosition
    }));
    setDraggingIcon(null);
  };

  // Function to initialize random velocities for icons
  const initializeIconVelocities = () => {
    const velocities = {};
    const positions = {};
    
    allDesktopItems.forEach((item, index) => {
      // Get current position or use initial position
      const currentPos = iconPositions[item.label] || {
        x: index >= 4 ? GRID_SIZE + 45 : 20,
        y: (index % 4) * (GRID_SIZE + 20)
      };

      positions[item.label] = currentPos;
      
      velocities[item.label] = {
        x: (Math.random() - 0.5) * 2, // Reduced from 5
        y: (Math.random() - 0.5) * 2, // Reduced from 5
        rotation: (Math.random() - 0.5) * 15, // Reduced from 30
        speed: 0.2 + Math.random() * 0.3, // Reduced initial speed
        phase: Math.random() * Math.PI * 2
      };
    });

    setIconPositions(positions);
    setIconVelocities(velocities);
  };

  // Function to update icon positions based on velocities
  const updateIconPositions = () => {
    if (!isMelting) return;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 40;
    const iconSize = 64;
    const time = Date.now() / 1000;

    setIconPositions(prev => {
      const newPositions = { ...prev };
      
      Object.keys(iconVelocities).forEach(label => {
        const velocity = iconVelocities[label];
        const position = prev[label] || { x: 0, y: 0, rotation: 0 };

        // Gentler wave motion
        const waveX = Math.sin(time + velocity.phase) * 10 * velocity.speed;
        const waveY = Math.cos(time + velocity.phase) * 10 * velocity.speed;

        let newX = position.x + (velocity.x + waveX) * velocity.speed;
        let newY = position.y + (velocity.y + waveY) * velocity.speed;
        let newRotation = (position.rotation || 0) + velocity.rotation * 0.5; // Slower rotation

        // Bounce with less acceleration
        if (newX < 0 || newX > screenWidth - iconSize) {
          velocity.x *= -1;
          velocity.speed *= 1.05; // Reduced from 1.1
        }
        if (newY < 0 || newY > screenHeight - iconSize) {
          velocity.y *= -1;
          velocity.speed *= 1.05;
        }

        newPositions[label] = {
          x: newX,
          y: newY,
          rotation: newRotation
        };
      });

      return newPositions;
    });

    // Slower chaos increase
    setIconVelocities(prev => {
      const newVelocities = { ...prev };
      Object.keys(newVelocities).forEach(label => {
        if (newVelocities[label].speed < 8) { // Reduced max speed from 15
          newVelocities[label].speed *= 1.005; // Reduced from 1.01
        }
      });
      return newVelocities;
    });

    animationFrameRef.current = requestAnimationFrame(updateIconPositions);
  };

  // Handle system crash with ramping chaos
  const handleSystemCrash = () => {
    setIsMelting(true);
    initializeIconVelocities();
    updateIconPositions();
    
    // First timeout: Show restart screen but keep melting
    setTimeout(() => {
      setShowRestartScreen(true);
    }, 3500);

    // Second timeout: Stop melting animations after restart screen is shown
    setTimeout(() => {
      setIsMelting(false);
      cancelAnimationFrame(animationFrameRef.current);
    }, 3700); // Slightly longer to ensure animations complete
  };

  // Clean up animation frame
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Add these handlers
  const handleMouseDown = (e) => {
    // Only start selection if clicking directly on the desktop (not icons)
    if (e.target === desktopContentRef.current) {
      setIsSelecting(true);
      const rect = desktopContentRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setSelectionStart({ x, y });
      setSelectionEnd({ x, y });
    }
  };

  const handleMouseMove = (e) => {
    if (isSelecting && desktopContentRef.current) {
      const rect = desktopContentRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setSelectionEnd({ x, y });
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  // Calculate selection rectangle dimensions
  const getSelectionRect = () => {
    return {
      left: Math.min(selectionStart.x, selectionEnd.x),
      top: Math.min(selectionStart.y, selectionEnd.y),
      width: Math.abs(selectionEnd.x - selectionStart.x),
      height: Math.abs(selectionEnd.y - selectionStart.y)
    };
  };

  // Add cleanup effect
  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <MeltingDesktop className={isMelting ? 'melting' : ''}>
      <WatermarkContainer className={isMelting ? 'melting' : ''}>
        <img src="/images/dOSlogo.svg" alt="dOS Logo" />
        <span>DavidOS</span>
      </WatermarkContainer>
      <DesktopContent 
        ref={desktopContentRef}
        className={isMelting ? 'melting' : ''}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        {allDesktopItems.map((item, index) => {
          let initialX = 20; // Base padding from left
          let initialY = index * (GRID_SIZE + 20); // Add 20px extra vertical spacing

          // For indices 4 and above, shift to second column
          if (index >= 4) {
            initialX = GRID_SIZE + 45; // Move to second column
            initialY = (index - 4) * (GRID_SIZE + 20); // Reset Y position for second column with extra spacing
          }

          return (
            <DesktopIcon
              key={index}
              index={index}
              icon={item.icon}
              label={item.label}
              isSelected={selectedIcon === item.label}
              onClick={() => handleIconClick(item)}
              onDoubleClick={() => handleIconDoubleClick(item)}
              onContextMenu={(e) => handleIconContextMenu(e, item)}
              enabled={item.enabled}
              className={`desktop-icon ${isMelting ? 'melting' : ''}`}
              draggable={!isMelting}
              onDragStart={(e) => handleDragStart(e, item)}
              onDrag={(e) => handleDrag(e, item)}
              onDragEnd={(e) => handleDragEnd(e, item)}
              style={{
                position: 'absolute',
                left: (iconPositions[item.label]?.x || initialX) + 'px',
                top: (iconPositions[item.label]?.y || initialY) + 'px',
                transform: isMelting ? 
                  `translate(${Math.random() * 10 - 5}px, ${Math.random() * 10 - 5}px) rotate(${iconPositions[item.label]?.rotation || 0}deg)` :
                  `rotate(${iconPositions[item.label]?.rotation || 0}deg)`,
                zIndex: draggingIcon === item.label ? '1000' : '1',
              }}
            />
          );
        })}
        
        {/* Add MP3 Player icon separately */}
        <DesktopIcon
          key="mp3player"
          icon="/images/mp3player.svg"
          label="MP3 Player"
          isSelected={selectedIcon === 'MP3 Player'}
          onClick={() => handleIconClick({ label: 'MP3 Player' })}
          onDoubleClick={() => handleIconDoubleClick({ label: 'MP3 Player', type: 'app' })}
          enabled={true}
          className={`desktop-icon ${isMelting ? 'melting' : ''}`}
          draggable={!isMelting}
          onDragStart={(e) => handleDragStart(e, { label: 'MP3 Player' })}
          onDrag={(e) => handleDrag(e, { label: 'MP3 Player' })}
          onDragEnd={(e) => handleDragEnd(e, { label: 'MP3 Player' })}
          style={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            transform: isMelting ? 
              `translate(${Math.random() * 10 - 5}px, ${Math.random() * 10 - 5}px) rotate(${iconPositions['MP3 Player']?.rotation || 0}deg)` :
              `rotate(${iconPositions['MP3 Player']?.rotation || 0}deg)`,
            zIndex: draggingIcon === 'MP3 Player' ? '1000' : '1',
          }}
        />

        {isSelecting && (
          <SelectionRectangle
            style={{
              left: `${getSelectionRect().left}px`,
              top: `${getSelectionRect().top}px`,
              width: `${getSelectionRect().width}px`,
              height: `${getSelectionRect().height}px`
            }}
          />
        )}
      </DesktopContent>

      <WindowsContainer className={isMelting ? 'melting' : ''}>
        {openWindows.map(window => {
          const position = windowPositions[window.id] || { left: '0px', top: '0px' };
          
          // Add position to each window component
          const windowProps = {
            key: window.id,
            onClose: () => handleCloseWindow(window.id),
            isActive: activeWindow === window.id,
            onActivate: () => handleActivateWindow(window.id),
            isMinimized: window.isMinimized,
            isMaximized: window.isMaximized,
            onMinimize: () => handleMinimize(window.id),
            onMaximize: () => handleMaximize(window.id),
            initialPosition: position // Add this prop
          };

          switch(window.name) {
            case 'Notepad':
              return (
                <Notepad
                  {...windowProps}
                  defaultWidth={window.defaultWidth}
                  defaultHeight={window.defaultHeight}
                  content={window.content}
                  onContentChange={(newContent) => handleContentChange(window.id, newContent)}
                  onSaveFile={(filename, content) => handleSaveFile(filename, content)}
                />
              );
            case 'Command Prompt':
              return (
                <CMD
                  {...windowProps}
                  onOpenAbout={() => {
                    const newWindow = {
                      id: Date.now(),
                      name: 'DavidOSInfo',
                      isMinimized: false,
                      isMaximized: false
                    };
                    setOpenWindows(prev => [...prev, newWindow]);
                    setActiveWindow(newWindow.id);
                  }}
                  onOpenAppleFarm={() => {
                    const newWindow = {
                      id: Date.now(),
                      name: 'AppleFarmInfo',
                      isMinimized: false,
                      isMaximized: false
                    };
                    setOpenWindows(prev => [...prev, newWindow]);
                    setActiveWindow(newWindow.id);
                  }}
                  onSystemCrash={handleSystemCrash}
                  isMelting={isMelting}
                  mp3Ref={mp3Ref}
                />
              );
            case 'About':
              return (
                <About
                  {...windowProps}
                />
              );
            case 'Recycle Bin':
              return (
                <RecycleBinWindow
                  {...windowProps}
                />
              );
            case 'Projects':
              return (
                <ProjectsFolder
                  {...windowProps}
                  onFileOpen={handleProjectFileOpen}
                />
              );
            case 'Resume':
              return (
                <Resume
                  {...windowProps}
                />
              );
            case 'About Me':
              return (
                <About
                  {...windowProps}
                />
              );
            case 'Chess':
              return (
                <ChessGame
                  {...windowProps}
                />
              );
            case 'DavidOSInfo':
              return (
                <DavidOSInfo
                  {...windowProps}
                />
              );
            case 'AppleFarmInfo':
              return (
                <AppleFarmInfo
                  {...windowProps}
                />
              );
            case 'Help':
              return (
                <Help
                  onClose={() => handleCloseWindow(window.id)}
                />
              );
            case 'MP3 Player':
              return (
                <MP3Player
                  {...windowProps}
                  defaultWidth="400px"
                  defaultHeight="300px"
                  mp3Ref={mp3Ref}
                />
              );
            default:
              return null;
          }
        })}
      </WindowsContainer>

      <Taskbar
        className={`taskbar ${isMelting ? 'melting' : ''}`}
        openWindows={openWindows}
        activeWindow={activeWindow}
        onWindowClick={handleActivateWindow}
        showStartMenu={showStartMenu}
        onStartClick={handleStartClick}
        onStartMenuItemClick={handleStartMenuItemClick}
        isMelting={isMelting}
      />

      {showContextMenu && selectedFile && (
        <ContextMenu
          x={contextMenuPosition.x}
          y={contextMenuPosition.y}
          onDelete={() => handleDeleteFile(selectedFile.label)}
        />
      )}

      {showRestartScreen && <RestartScreen onRestart={() => window.location.reload()} />}
    </MeltingDesktop>
  );
};

export default Desktop;